/**
 * AdminRolePreviewContext
 * Context toàn cục để chia sẻ previewRole giữa Topbar, Sidebar, usePermission
 * Chỉ admin mới có thể set previewRole.
 * State persist sessionStorage.
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { UserRole } from '@/config/navigation.config';

const PREVIEW_KEY = 'landscape_admin_preview_role';

interface AdminRolePreviewContextValue {
  previewRole: UserRole | null;
  isPreviewMode: boolean;
  setPreviewRole: (role: UserRole | null) => void;
  clearPreview: () => void;
}

const AdminRolePreviewContext = createContext<AdminRolePreviewContextValue>({
  previewRole: null,
  isPreviewMode: false,
  setPreviewRole: () => {},
  clearPreview: () => {},
});

interface AdminRolePreviewProviderProps {
  children: React.ReactNode;
  isAdmin: boolean;
}

export function AdminRolePreviewProvider({ children, isAdmin }: AdminRolePreviewProviderProps) {
  const [previewRole, setPreviewRoleState] = useState<UserRole | null>(() => {
    if (!isAdmin) return null;
    try {
      const cached = sessionStorage.getItem(PREVIEW_KEY);
      return cached ? (cached as UserRole) : null;
    } catch {
      return null;
    }
  });

  // Sync to sessionStorage
  useEffect(() => {
    if (!isAdmin) {
      try { sessionStorage.removeItem(PREVIEW_KEY); } catch { /* ignore */ }
      setPreviewRoleState(null);
      return;
    }
    try {
      if (previewRole) {
        sessionStorage.setItem(PREVIEW_KEY, previewRole);
      } else {
        sessionStorage.removeItem(PREVIEW_KEY);
      }
    } catch { /* ignore */ }
  }, [previewRole, isAdmin]);

  const setPreviewRole = useCallback((role: UserRole | null) => {
    if (!isAdmin) return;
    setPreviewRoleState(role);
  }, [isAdmin]);

  const clearPreview = useCallback(() => {
    setPreviewRoleState(null);
  }, []);

  return (
    <AdminRolePreviewContext.Provider value={{
      previewRole: isAdmin ? previewRole : null,
      isPreviewMode: isAdmin && previewRole !== null,
      setPreviewRole,
      clearPreview,
    }}>
      {children}
    </AdminRolePreviewContext.Provider>
  );
}

export function useAdminRolePreviewContext(): AdminRolePreviewContextValue {
  return useContext(AdminRolePreviewContext);
}
